import {forwardRef, useRef, useEffect, useState} from 'react'
/** @jsxImportSource @emotion/react */
import {css} from '@emotion/react'

import {useNavigate} from 'react-router-dom'


import Next from '@mui/icons-material/SkipNext'
import Pause from '@mui/icons-material/Pause'
import Skeleton from '@mui/material/Skeleton'
import Previous from '@mui/icons-material/SkipPrevious'
import PlayArrow from '@mui/icons-material/PlayArrow'
import CloseIcon from '@mui/icons-material/Close'
import IconButton from '@mui/material/IconButton'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import VolumeUpIcon from '@mui/icons-material/VolumeUp'
import VolumeOffIcon from '@mui/icons-material/VolumeOff'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'

import {SiApplemusic} from 'react-icons/si'
// import {PiSpotifyLogoFill} from 'react-icons/pi'


import TrackSlider from './TrackSlider'
import VolumeControl from './VolumeControl'

import {Image, FavoriteIconButton} from '../generics'



const styles = {
  root: css`
    z-index: 100;
    background-color: #242639;
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 40px;
    border-radius: 8px;
    box-shadow: rgba(0, 0, 0, 0.16) 0px 10px 36px 0px, rgba(0, 0, 0, 0.06) 0px 0px 0px 1px;
    width: 306px;
    height: 560px;
    color: #fff;
    overflow: hidden;
    
    @media (max-width: 600px) {
      box-shadow: none;
      align-items: center;
      justify-content: center;
      animation: none;
      height: 100%;
    }
  `,
  closeIcon: css`
    position: absolute;
    cursor: pointer;
    top: 5px;
    right: 5px;
    color: grey;
    z-index: 31;
  `,
  videoContainer: css`
    position: absolute;
    width: 100%;
    height: 100%;
    top: 0;
    left: 0;
    z-index: 20;
    background-color: black;
    border-radius: 8px;
    overflow: hidden;
    
    video {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  `,
  imageContainer: css`
    display: flex;
    justify-content: center;
    align-items: center;
    width: 130px;
    height: 130px;
    min-height: 130px;
    min-width: 130px;
    border-radius: 50%;
    box-shadow: 3px 15px 61px -18px rgba(255, 0, 149, 0.51);
    margin-bottom: 15px;
    position: relative;
  `,
  image: css`
    width: 130px;
    height: 130px;
    border-radius: 50%;
    object-fit: cover;
    animation: spin 5000ms linear infinite;
    z-index: 10;
  `,
  hole: css`
    position: absolute;
    background-color: #fff;
    width: 12px;
    height: 12px;
    border-radius: 50%;
    z-index: 11;
  `,
  musicServiceIcon: css`
    position: absolute;
    z-index: 11;
    width: 36px;
    height: 36px;
    bottom: -10px;
    right: 10px;
    color: #fc3c44;
    background-color: #fff;
    border-radius: 50%;
  `,
  infoContainer: css`
    width: 100%;
    margin-top: 15px;
    margin-bottom: 15px;
    display: flex;
    flex-direction: column;
    cursor: pointer;
    height: 46px;
    
    h3 {
      font-size: 1rem;
      font-weight: 500;
      line-height: 1.2;
      overflow: hidden;
      white-space: nowrap;
      text-overflow: ellipsis;
      color: #fff;
    }
    
    p {
      overflow: hidden;
      white-space: nowrap;
      text-overflow: ellipsis;
      color: grey;
    }
  `,
  buttonsContainer: css`
    display: flex;
    width: 100%;
    justify-content: flex-end;
    align-items: center;
    gap: 5px;
    
    button, a {
      height: 34px;
      width: 34px;
    }
  `,
  appleLink: css`
    padding: 0;
    display: inline-flex;
    align-items: center;
  `,
  trackSlider: css`
    width: 100%;
  `,
  controlsContainer: css`
    width: 100%;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    margin-top: 7px;
    margin-bottom: 7px;
    z-index: 30;
    padding-top: 20px;
  `,
  neumorphicButton: css`
    width: 70px;
    height: 70px;
    background-color: #242639;
    color: #fff;
    padding: 10px;
    border: none;
    outline: none;
    border-radius: 50%;
    transition: transform 0.2s ease-in-out, box-shadow 0.3s ease-in-out;
    box-shadow: 5px 5px 10px rgba(0, 0, 0, 0.3), 0px 0px 5px rgba(255, 0, 149, 0.6);
    cursor: pointer;

    &:active {
      transform: scale(0.95);
      box-shadow: inset 5px 5px 10px rgba(0, 0, 0, 0.6), inset 0px 0px 5px rgba(255, 0, 149, 1);
    }
  `,
  largeControls: css`
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 15px;
    margin-bottom: 15px;
  `,
  volumeControls: css`
    width: 100%;
    display: flex;
    align-items: center;
    padding: 10px 20px;

    @media (max-width: 600px) {
      padding-top: 50px;
    }
  `,
  volumeContainer: css`
    width: 100%;
    flex-grow: 1;
  `,
  // Keyframes can also be defined locally
  '@keyframes spin': css`
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  `,
  '@keyframes slide-in': css`
    from { transform: translateX(100%); }
    to { transform: translateX(0); }
  `,
  '@keyframes slide-up': css`
    from { transform: translateY(100%); }
    to { transform: translateY(0); }
  `,
}






const MediaPlayerExpanded = forwardRef((props, ref) => {

  const navigate = useNavigate()

  const videoRef = useRef()
  const closeIconRef = useRef()
  const minimizeIconRef = useRef()


  const [canPlay, setCanPlay] = useState(false)


  const {
    track,
    imageRef,
    audioRef,
    isMuted,
    volume,
    setVolume,
    setIsMuted,
    isPlaying,
    handleEnd,
    handleClose,
    playNextTrack,
    minimizePlayer,
    isMobileDevice,
    handlePlayClick,
    playPreviousTrack
  } = props



  const {
    name, 
    image,
    video,
    composer,
    composerId, 
    composerType,
    // spotifyLink,
    appleMusicLink,
    imageDerivatives
  } = track.attributes || {}


  const isAppleTrack = Boolean(appleMusicLink)



  useEffect(() => {
    if (imageRef.current) {
      const animationState = isPlaying ? 'running' : 'paused'
      imageRef.current.style.animationPlayState = animationState
    }
  }, [isPlaying, imageRef])




  useEffect(() => {
    setCanPlay(false)


    function handleCanPlay() {
      setCanPlay(true)
    }

    const videoElement = videoRef.current

    videoElement?.load()
    videoElement?.addEventListener('canplay', handleCanPlay)


    return () => {
      videoElement?.removeEventListener('canplay', handleCanPlay)
    }
  }, [video])




  useEffect(() => {
    if (!canPlay) return


    async function togglePlay() {
      if (isPlaying) {
        videoRef.current.play()
      } else {
        await videoRef.current.pause()
      }
    }

    togglePlay()
  }, [isPlaying, canPlay])




  useEffect(() => {
    videoRef.current.muted = isMuted
  }, [isMuted])




  useEffect(() => {
    videoRef.current.volume = volume / 100
  }, [volume])



  // Change the color of the close icon depending on if a video 
  // or track is playing
  useEffect(() => {
    const closeIcon = closeIconRef.current
    const minimizeIcon = minimizeIconRef.current

    if (video) {
      closeIcon.style.color = '#fff'
      minimizeIcon.style.color = '#fff'
    } else {
      closeIcon.style.color = 'grey'
      minimizeIcon.style.color = 'black'
    }
  }, [video])




  function navigateToComposersPage(e) {
    e.stopPropagation()

    try {
      if (['event', 'performance'].includes(composerType.toLowerCase())) {
        navigate(`/performances/${composerId}`)
      } else {
        navigate(`/${composerType.toLowerCase()}s/${composerId}`)
      }
      
      // For small devices, minmize the player to show the navigation
      if (isMobileDevice) {
        minimizePlayer()
      }

      return
    } catch (error) {
      console.log(error)
    }
  }



  function toggleMute() {
    setIsMuted(!isMuted)
  }



  return (
    <div ref={ref} id='audio-player' css={styles.root}>
      <CloseIcon ref={closeIconRef} css={styles.closeIcon} onClick={handleClose} />

      <div 
        css={styles.videoContainer}
        style={{
          display: video ? 'block' : 'none',
          borderRadius: isMobileDevice ? '0' : '8px'
        }}
      >
        <video 
          id='video-player'
          ref={videoRef}
          width='100%' 
          muted 
          autoPlay
          playsInline
          onEnded={handleEnd}
        >
          <source src={video} type='video/mp4' />
        </video>
      </div>

    

      {!video &&
        <>
          <div css={styles.imageContainer}>
            <Image
              id='audio-player-image'
              alt='track'
              src={imageDerivatives?.small || image}
              ref={imageRef}
              loading='eager'
              style={{overflow: 'hidden'}}
              css={styles.image} 
            /> 
            
            <div css={styles.hole} />


            {isAppleTrack &&
              <SiApplemusic css={styles.musicServiceIcon} /> 
            }
          </div>



          <div css={styles.infoContainer}>
            <h3>
              {Boolean(name) ? name : 
                <Skeleton sx={{fontSize: '1rem', bgcolor: '#d3d3d3'}} width={150} 
                />
              }
            </h3>

            <p>
              {(Boolean(name) || Boolean(composer)) ? composer : 
                <Skeleton sx={{fontSize: '0.9rem', bgcolor: '#d3d3d3'}} width={100} />
              }
            </p>
          </div>



          <div css={styles.buttonsContainer}>
            {!isAppleTrack &&         
              <FavoriteIconButton id={track.id} type='track' color='#fff' />
            }


            {isAppleTrack &&
              <IconButton size='small'>
                <a css={styles.appleLink} href={appleMusicLink} target='_blank' rel='noreferrer'>
                  <SiApplemusic size='23px' color='#fff' /> 
                </a>
              </IconButton>
            }

            <IconButton onClick={navigateToComposersPage} size='small'>
              <OpenInNewIcon color='secondary' />
            </IconButton>
          </div>



          <div css={styles.trackSlider}>
            <TrackSlider 
              audioRef={audioRef} 
              trackId={track.id}
              trackColor='#4834AF'
              thumbColor='#4834AF'
            />
          </div>
        </>
      }



      <div css={styles.controlsContainer}>
        <div css={styles.largeControls}>
          <IconButton onClick={playPreviousTrack}>
            <Previous color='secondary' />
          </IconButton>


          <IconButton disableRipple css={styles.neumorphicButton} onClick={handlePlayClick}>
            {isPlaying 
              ? <Pause fontSize='large' color='secondary' /> 
              : <PlayArrow fontSize='large' color='secondary' />
            }
          </IconButton>


          <IconButton onClick={playNextTrack}>
            <Next color='secondary' />
          </IconButton>
        </div>




        <div
          css={styles.volumeControls}
          style={{
            gap: video ? '5px' : '0px',
            justifyContent: video ? 'center' : 'space-between'
          }} 
        >
          {!video &&
            <div css={styles.volumeContainer}>
              <VolumeControl
                volume={volume}
                isMuted={isMuted}
                callback={setVolume}
                trackColor='#4834AF'
                thumbColor='#4834AF'
                setIsMuted={setIsMuted}
              />
            </div>
          }


          {video &&
            <IconButton css={styles.button} onClick={toggleMute}>
              {isMuted 
                ? <VolumeOffIcon color='secondary' /> 
                : <VolumeUpIcon  color='secondary' />
              }
            </IconButton>
          }


          {video &&
            <IconButton 
              css={styles.button} onClick={navigateToComposersPage}>
              <OpenInNewIcon color='secondary' />
            </IconButton>
          }
        </div>


        <IconButton 
          ref={minimizeIconRef}
          onClick={minimizePlayer} 
          css={styles.button}
          style={{
            position: 'absolute',
            bottom: '5px',
            transform: isMobileDevice ? 'rotate(180deg)' : 'rotate(0)',
            marginBottom: video ? '0px' : '5px'
          }} 
        >
          <ExpandLessIcon sx={{color: 'lightgrey'}} />
        </IconButton>
      </div>
    </div>
  )
})




export default MediaPlayerExpanded


